/**
 * LateCheckinDialogComponent - Warning dialog for late (escalating) check-ins
 *
 * Shown when a user attempts to check in while their check-in is in the
 * 'escalating' status (contacts have already been alerted). Warns the user
 * that contacts were notified, lets them add an optional context note
 * (max 200 characters) with common suggestions, and confirms before
 * resolving. Cancelling keeps the check-in unresolved.
 *
 * Presented via ModalController from CheckInService.resolveCheckIn().
 * Dismiss roles:
 * - 'confirm' with { confirmed: true, note? } - resolve and notify contacts
 * - 'cancel' with { confirmed: false } - keep check-in unresolved
 *
 * @see WI-049 - Late Check-In Recovery
 * @see CheckInService.resolveCheckIn() - Integration point
 * @see functions/src/escalation/sendResolvedNotification.ts - Server-side flow
 */

import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
  IonTextarea,
  IonChip,
  IonLabel,
  ModalController,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { closeOutline, warningOutline } from 'ionicons/icons';
import { devLog } from '@core';

/**
 * Maximum length for the optional resolution note (WI-049 Section 1.3 #8)
 */
export const LATE_CHECKIN_NOTE_MAX_LENGTH = 200;

/**
 * Result returned when the dialog is dismissed
 */
export interface LateCheckinDialogResult {
  /** True if the user confirmed the late check-in */
  confirmed: boolean;
  /** Optional note to send to contacts (trimmed, max 200 chars) */
  note?: string;
}

@Component({
  selector: 'app-late-checkin-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonIcon,
    IonTextarea,
    IonChip,
    IonLabel,
  ],
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-title i18n="Late check-in dialog title@@checkin.late.title">Check In (Late)</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="dismiss()" i18n-aria-label="Cancel late check-in button@@checkin.late.cancelAriaLabel" aria-label="Cancel late check-in">
            <ion-icon name="close-outline" slot="icon-only"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding">
      <div class="warning-banner" role="alert">
        <ion-icon name="warning-outline" aria-hidden="true"></ion-icon>
        <p i18n="Warning message on late check-in dialog@@checkin.late.warningMessage">
          Your contacts have already been notified that you missed your
          check-in. They will receive an update that you're safe.
        </p>
      </div>

      <div class="note-section">
        <label class="note-label" for="late-checkin-note" i18n="Note field label on late check-in dialog@@checkin.late.noteLabel">
          Optional note (will be sent to contacts)
        </label>
        <ion-textarea
          id="late-checkin-note"
          [(ngModel)]="note"
          i18n-placeholder="Note field placeholder on late check-in dialog@@checkin.late.notePlaceholder"
          placeholder="e.g., 'Fell asleep, sorry!'"
          [rows]="3"
          [maxlength]="maxNoteLength"
          i18n-aria-label="Note field aria label on late check-in dialog@@checkin.late.noteAriaLabel"
          aria-label="Optional note for contacts">
        </ion-textarea>
        <div class="character-counter" aria-live="polite" i18n="Character counter on late check-in dialog@@checkin.late.charCounter">
          {{ noteLength }} / {{ maxNoteLength }} characters
        </div>

        <div class="note-suggestions">
          @for (suggestion of noteSuggestions; track suggestion) {
            <ion-chip
              [outline]="note !== suggestion"
              (click)="applySuggestion(suggestion)"
              [attr.aria-label]="'Use note: ' + suggestion">
              <ion-label>{{ suggestion }}</ion-label>
            </ion-chip>
          }
        </div>
      </div>

      <ion-button
        expand="block"
        color="primary"
        class="confirm-button"
        [disabled]="isSubmitting"
        (click)="confirmCheckin()">
        {{ isSubmitting ? 'Checking in...' : 'Check In & Notify Contacts' }}
      </ion-button>

      <p class="preview-hint" i18n="Contact message preview hint on late check-in dialog@@checkin.late.previewHint">
        Your contacts will receive: "{{ userName || 'Your friend' }} is safe"
      </p>
    </ion-content>
  `,
  styles: [
    `
      .warning-banner {
        display: flex;
        align-items: flex-start;
        gap: var(--spacing-sm);
        padding: var(--spacing-md);
        margin-bottom: var(--spacing-lg);
        background: rgba(var(--ion-color-warning-rgb, 245, 158, 11), 0.12);
        border-left: 4px solid var(--ion-color-warning);
        border-radius: var(--radius-md);

        ion-icon {
          font-size: var(--font-size-xxl);
          color: var(--ion-color-warning);
          flex-shrink: 0;
        }

        p {
          margin: 0;
          font-size: var(--font-size-sm);
          line-height: var(--line-height-normal);
          color: var(--ion-text-color);
        }
      }

      .note-section {
        margin-bottom: var(--spacing-lg);

        .note-label {
          display: block;
          font-size: var(--font-size-sm);
          font-weight: var(--font-weight-medium);
          margin-bottom: var(--spacing-xs);
        }

        .character-counter {
          font-size: var(--font-size-xs);
          color: var(--ion-color-medium);
          text-align: right;
          margin-top: var(--spacing-xs);
        }
      }

      .note-suggestions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-xs);
        margin-top: var(--spacing-sm);

        ion-chip {
          min-height: 44px;
        }
      }

      .confirm-button {
        min-height: 48px;
        margin-top: var(--spacing-md);
      }

      .preview-hint {
        font-size: var(--font-size-sm);
        color: var(--ion-color-medium);
        text-align: center;
        margin-top: var(--spacing-md);
      }
    `,
  ],
})
export class LateCheckinDialogComponent {
  private modalController = inject(ModalController);

  /** Name of the user checking in (shown in the contact message preview) */
  @Input() userName = '';

  /** ID of the escalating check-in being resolved */
  @Input() checkInId = '';

  /** Optional note text (bound to textarea, max 200 chars) */
  @Input() note = '';

  /** Prevents double submission while dismissing */
  isSubmitting = false;

  readonly maxNoteLength = LATE_CHECKIN_NOTE_MAX_LENGTH;

  /** Common reasons for missing a check-in (WI-049 Section 1.4 guidance) */
  readonly noteSuggestions: readonly string[] = ['Fell asleep', 'Phone died'];

  constructor() {
    addIcons({ closeOutline, warningOutline });
  }

  /** Current note length (after enforcing the max) */
  get noteLength(): number {
    return this.note?.length ?? 0;
  }

  /**
   * Apply a suggested note (replaces current note text)
   */
  applySuggestion(suggestion: string): void {
    this.note = suggestion;
  }

  /**
   * Confirm the late check-in and dismiss with the trimmed note
   */
  async confirmCheckin(): Promise<void> {
    if (this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;

    const trimmed = this.note?.trim().slice(0, LATE_CHECKIN_NOTE_MAX_LENGTH);
    const result: LateCheckinDialogResult = {
      confirmed: true,
      note: trimmed || undefined,
    };

    devLog('[LateCheckinDialog] Confirmed late check-in', {
      checkInId: this.checkInId,
      hasNote: !!result.note,
    });

    await this.modalController.dismiss(result, 'confirm');
  }

  /**
   * Cancel the late check-in - check-in stays unresolved
   */
  async dismiss(): Promise<void> {
    devLog('[LateCheckinDialog] Cancelled late check-in', {
      checkInId: this.checkInId,
    });

    const result: LateCheckinDialogResult = { confirmed: false };
    await this.modalController.dismiss(result, 'cancel');
  }
}
